
import React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { MdLogout, MdChevronRight, MdSettings } from 'react-icons/md';
import ToastMessage from './ToastMessage';
import { API } from '../api';
import { useUserContext } from '../context/AuthContext';
import { leftSidebarLinks } from "../constants";
import { getMediaUrl } from "../lib/utils";

const LeftSide = ({ user }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setIsAuthenticated, setAuthUser } = useUserContext();

  const logout = async (e) => {
    e.preventDefault();

    try {
      const res = await API.post('/logout');

      setIsAuthenticated(false);
      setAuthUser(null);

      navigate('/auth', { replace: true });

      ToastMessage(res.data.message);
    } catch (error) {
      console.error(error);
    }
  };

  if (!user) return null;

  const NAVBAR_HEIGHT = 72;
  const isAccountActive = pathname === '/settings/account' || pathname.startsWith('/settings/account/');

  return (
    <aside
      className="sticky top-[72px] hidden w-[270px] shrink-0 flex-col border-r border-gray-100 bg-white lg:flex"
      style={{ height: `calc(100vh - ${NAVBAR_HEIGHT}px)` }}
    >
      {/* ================= PROFILE ================= */}

      <div className="px-5 pt-6">
        <Link
          to={`/profile/${user.id}`}
          className="group flex items-center gap-3 rounded-xl px-2 py-2 transition hover:bg-gray-50"
        >
          <div className="relative shrink-0">
            <img
              src={getMediaUrl(user.profile_img)}
              alt={`${user.firstname} ${user.lastname}`}
              className="h-11 w-11 rounded-full object-cover ring-1 ring-gray-200"
            />

            <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-green-500" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900">
              {user.firstname} {user.lastname}
            </p>

            <p className="mt-0.5 text-xs text-gray-400">My profile</p>
          </div>

          <MdChevronRight
            size={18}
            className="text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-purple-500"
          />
        </Link>
      </div>

      {/* ================= DIVIDER ================= */}

      <div className="mx-5 mt-5 border-t border-gray-100" />

      {/* ================= MENU TITLE ================= */}

      <div className="mb-2 mt-5 px-7">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400">
          Navigation
        </span>
      </div>

      {/* ================= NAVIGATION ================= */}

      <nav className="flex-1 overflow-y-auto px-4">
        <ul className="space-y-1">
          {leftSidebarLinks.map((link) => {
            const isActive = pathname === link.route || pathname.startsWith(`${link.route}/`);

            return (
              <li key={link.label}>
                <NavLink
                  to={link.route}
                  className={`group relative flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-purple-600 text-white shadow-sm shadow-purple-100'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {/* Icon */}

                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all ${
                      isActive ? 'bg-white/15' : 'bg-gray-50 group-hover:bg-white'
                    }`}
                  >
                    <img
                      src={link.imgURL}
                      alt={link.label}
                      className={`h-[18px] w-[18px] object-contain transition-all ${
                        isActive
                          ? 'brightness-0 invert'
                          : 'opacity-65 group-hover:opacity-100'
                      }`}
                    />
                  </div>

                  <span className="flex-1">{link.label}</span>

                  {isActive && (
                    <MdChevronRight size={18} className="text-white/70" />
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* ================= BOTTOM ACCOUNT ================= */}

      <div className="border-t border-gray-100 px-4 py-4">
        <div className="mb-2 flex items-center gap-3 px-3 py-2">
          <img
            src={getMediaUrl(user.profile_img)}
            alt="Profile"
            className="h-8 w-8 rounded-full object-cover"
          />

          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-gray-700">
              {user.firstname} {user.lastname}
            </p>

            <p className="mt-0.5 text-[11px] text-gray-400">Signed in</p>
          </div>
        </div>

        {/* ================= ACCOUNT SETTINGS ================= */}

        <NavLink
          to="/settings/account"
          className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.98] ${
            isAccountActive
              ? 'bg-purple-600 text-white shadow-sm shadow-purple-100'
              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-lg transition-all ${
              isAccountActive
                ? 'bg-white/15 text-white'
                : 'bg-gray-50 text-gray-400 group-hover:bg-purple-50 group-hover:text-purple-600'
            }`}
          >
            <MdSettings size={18} />
          </div>

          <span className="flex-1 text-left">Account</span>

          {isAccountActive && (
            <MdChevronRight size={18} className="text-white/70" />
          )}
        </NavLink>

        {/* ================= LOGOUT ================= */}

        <button
          type="button"
          onClick={logout}
          className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-500 transition-all duration-200 hover:bg-gray-50 hover:text-gray-900 active:scale-[0.98]"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50 text-gray-400 transition-all group-hover:bg-red-50 group-hover:text-red-500">
            <MdLogout size={18} />
          </div>

          <span className="flex-1 text-left">Log out</span>
        </button>
      </div>
    </aside>
  );
};

export default LeftSide;
